import { useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { motion } from "framer-motion";
import { useTenantTranslation } from "hooks/formatters";
import {
  MEDAL_COLORS,
  DEFAULT_MEDAL_COLOR,
} from "components/common/plots/colorSets";
import { useFormatNumber } from "src/utils/formatNumber";

interface CrewComplianceScore {
  crewId: string;
  compliancePercent: number;
}

type CompliancePodiumProps = Readonly<{
  crewScores: CrewComplianceScore[];
  overallCompliancePercent: number | undefined;
  embedded?: boolean;
}>;

const PODIUM_HEIGHTS: Record<number, number> = {
  1: 50,
  2: 38,
  3: 28,
};

const EMBEDDED_PODIUM_HEIGHTS: Record<number, number> = {
  1: 90,
  2: 70,
  3: 52,
};

const DEFAULT_PODIUM_HEIGHT = 18;
const EMBEDDED_DEFAULT_PODIUM_HEIGHT = 38;

const PODIUM_GROW_DURATION = 0.3;
const POPIN_DELAY = 0.2;

type PodiumPositionProps = Readonly<{
  crewId: string;
  rank: number;
  compliancePercent: number;
  crewLabel: string;
  embedded: boolean;
}>;

const PodiumPosition = ({
  crewId,
  rank,
  compliancePercent,
  crewLabel,
  embedded,
}: PodiumPositionProps) => {
  const formatNumber = useFormatNumber();
  const medalColor = MEDAL_COLORS[rank] ?? DEFAULT_MEDAL_COLOR;
  const podiumHeight =
    (embedded ? EMBEDDED_PODIUM_HEIGHTS : PODIUM_HEIGHTS)[rank] ??
    (embedded ? EMBEDDED_DEFAULT_PODIUM_HEIGHT : DEFAULT_PODIUM_HEIGHT);
  const medalSize = embedded ? 54 : 36;
  const medalIconSize = embedded ? 30 : 20;
  const barWidth = embedded ? 92 : 80;
  const baseDelay = 0.15 * (rank - 1);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        alignSelf: "flex-end",
      }}
    >
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          delay: baseDelay + POPIN_DELAY,
          duration: 0.3,
          type: "spring",
          stiffness: 400,
          damping: 15,
        }}
      >
        <Box
          sx={{
            width: medalSize,
            height: medalSize,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: medalColor,
            color: "white",
            mb: 0.5,
          }}
        >
          <EmojiEventsIcon sx={{ fontSize: medalIconSize }} />
        </Box>
      </motion.div>

      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          delay: baseDelay + POPIN_DELAY,
          duration: 0.3,
          type: "spring",
          stiffness: 400,
          damping: 15,
        }}
      >
        <Typography
          variant={embedded ? "body1" : "body2"}
          color="text.secondary"
          sx={{ fontWeight: 700, textAlign: "center" }}
        >
          {crewLabel} {crewId}
        </Typography>
        <Typography
          variant={embedded ? "body2" : "caption"}
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            mb: 0.5,
            textAlign: "center",
            display: "block",
          }}
        >
          {formatNumber(1, true)(compliancePercent)}%
        </Typography>
      </motion.div>

      <motion.div
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={{
          delay: baseDelay,
          duration: PODIUM_GROW_DURATION,
          ease: [0.34, 1.56, 0.64, 1],
        }}
        style={{ originY: 1 }}
      >
        <Box
          sx={{
            width: barWidth,
            height: podiumHeight + 8,
            bgcolor: medalColor,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            variant={embedded ? "h4" : "h5"}
            sx={{
              fontWeight: 800,
              color: "white",
            }}
          >
            {rank}
          </Typography>
        </Box>
      </motion.div>
    </Box>
  );
};

export const CompliancePodium = ({
  crewScores,
  overallCompliancePercent,
  embedded = false,
}: CompliancePodiumProps) => {
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();

  const rankedCrews = useMemo(() => {
    return [...crewScores].sort(
      (a, b) => b.compliancePercent - a.compliancePercent
    );
  }, [crewScores]);

  const podiumOrder = [
    rankedCrews[1],
    rankedCrews[0],
    rankedCrews[2],
    ...rankedCrews.slice(3),
  ].filter((crew): crew is CrewComplianceScore => crew !== undefined);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        p: 2,
        ...(embedded
          ? { justifyContent: "space-around", width: "100%" }
          : { border: 1, borderColor: "grey.300", borderRadius: 1 }),
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ minWidth: embedded ? 130 : 100 }}
      >
        <Typography variant={embedded ? "h6" : "body1"} color="text.secondary">
          {t("complianceScore")}
        </Typography>
        <Typography
          variant="h2"
          color="primary"
          sx={embedded ? { fontSize: "2.75rem", lineHeight: 1.1 } : undefined}
        >
          {overallCompliancePercent !== undefined
            ? `${formatNumber(1, true)(overallCompliancePercent)}%`
            : "-"}
        </Typography>
      </Stack>

      {podiumOrder.length > 0 && (
        <Stack direction="row" spacing={0.5} alignItems="flex-end">
          {podiumOrder.map((crew) => {
            const rank = rankedCrews.indexOf(crew) + 1;
            return (
              <PodiumPosition
                key={crew.crewId + rank.toString()}
                crewId={crew.crewId}
                rank={rank}
                compliancePercent={crew.compliancePercent}
                crewLabel={t("crew")}
                embedded={embedded}
              />
            );
          })}
        </Stack>
      )}
    </Box>
  );
};
